/**
 * <AlertCard> — a generic, severity-tinted alert (molecule, L0.1).
 *
 * A `<Card>` (tinted `surface-subtle` surface + a colored left rail) holding a `<Banner>` message
 * with a leading status icon, and — when an `accordionBody` is passed — an expand affordance
 * revealing it (via the `<Accordion>`). Fully generic: it knows nothing about engagement/domain
 * data; the caller composes the `message` (a node, so it can carry emphasis) and the optional
 * `accordionBody`. Reuses `<Card>` / `<Banner>` / `<Accordion>` — no hand-rolled surface, no hex.
 *
 * Quatre degrés, d'un seul composant : les deux qui alertent (`alert`, `warning`) et les deux qui
 * informent (`info`, `success`) — les bandeaux d'activation, de pause et de reprise sont des
 * avis, pas des alarmes, et ils n'ont pas besoin d'un `NoticeCard` à côté.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { Text } from "../../atoms/typography/Text"
import { Icon, type IconName } from "../../atoms/icons/Icon"
import { Banner } from "../../atoms/Banner"
import { Card } from "../Card"
import { Accordion } from "../Accordion"

export type AlertCardSeverity = "alert" | "warning" | "info" | "success"

/**
 * Le filet porte la couleur pleine, l'encre porte le contraste. `alert` et `warning` gardent
 * l'encre qu'ils ont toujours eue — les changer déplacerait des écrans déjà relus ; les deux
 * degrés ajoutés prennent d'emblée l'encre foncée là où le vert plein passerait sous l'AA.
 */
const SEVERITY: Record<AlertCardSeverity, { rail: SemanticColorKey; ink: SemanticColorKey; icon: IconName }> = {
    alert: { rail: "danger", ink: "danger", icon: "exclamation-circle" },
    warning: { rail: "warning", ink: "warning", icon: "exclamation-circle" },
    info: { rail: "info", ink: "info", icon: "info-circle" },
    success: { rail: "success", ink: "success-strong", icon: "check-circle" },
}

export type AlertCardProps = {
    /** `alert` → rouge, `warning` → orange, `info` → marine, `success` → vert progression. */
    severity: AlertCardSeverity
    /**
     * Titre au-dessus du message — rien par défaut. L'avis d'une page d'activation en porte un
     * (« Une pause est en place ») là où un bandeau d'alerte n'a qu'une phrase.
     */
    title?: string
    message: React.ReactNode
    /** Les gestes de l'avis, sous le message — un ou deux `<Button>`. Rien par défaut. */
    actions?: React.ReactNode
    /** Present → the card becomes expandable, revealing this on tap. */
    accordionBody?: React.ReactNode
    testID?: string
}

export function AlertCard(props: AlertCardProps) {
    const { severity, title, message, actions, accordionBody, testID } = props
    const { rail, ink, icon } = SEVERITY[severity]
    const hasBody = accordionBody != null

    // Le titre entre DANS le message : le bandeau garde son glyphe de tête, et le texte reste
    // aligné sous lui au lieu de repartir du bord de la carte.
    const body =
        title == null ? (
            message
        ) : (
            <Stack gap="1">
                <Text size="text-md" weight="bold">
                    {title}
                </Text>
                {typeof message === "string" ? <Text color="text-muted">{message}</Text> : message}
            </Stack>
        )

    const header = (open: boolean) => (
        <Row align="center" justify="between" gap="2">
            <Banner icon={icon} color={ink} message={body} align="start" style={styles.banner} />
            {hasBody && <Icon name={open ? "chevron-up" : "chevron-down"} size={14} color="text-muted" />}
        </Row>
    )

    if (!hasBody) {
        return (
            <Card testID={testID} background="surface-subtle" leftBorderColor={rail} p="3">
                {actions == null ? (
                    header(false)
                ) : (
                    <Stack gap="4">
                        {header(false)}
                        {actions}
                    </Stack>
                )}
            </Card>
        )
    }

    return (
        <Card testID={testID} background="surface-subtle" leftBorderColor={rail} p="3">
            <Stack gap="4">
                <Accordion header={header}>
                    <View style={styles.body}>{accordionBody}</View>
                </Accordion>
                {actions}
            </Stack>
        </Card>
    )
}

const styles = StyleSheet.create((theme) => ({
    banner: {
        flex: 1,
    },
    body: {
        paddingTop: theme.spacing["2"],
    },
}))
